import { Controller } from "@hotwired/stimulus"
import { ArcadeStage, arcadePalette } from "../utils/arcade_stage"

// Le serpent, version contrats : chaque « contrat » avalé allonge le portefeuille d'un
// anneau et accélère la partie. On perd contre un mur ou contre soi-même.
//
// Tout est peint dans un seul <canvas> : une grille de 24 × 24 en <div> repeindrait 576
// nœuds à chaque pas, pour un jeu qui tourne à dix images par seconde et finirait par faire
// ramer l'onglet du tableau de bord.
const COLUMNS = 24
const ROWS = 24
const START_INTERVAL = 130
const MIN_INTERVAL = 62
const SPEED_STEP = 3

const DIRECTIONS = {
  ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
  // ZQSD (azerty) et WASD (qwerty) : la même touche physique porte les deux.
  z: [0, -1], w: [0, -1], s: [0, 1], q: [-1, 0], a: [-1, 0], d: [1, 0],
}

export default class extends Controller {
  static values = { labels: Object, scoresUrl: String }

  start() {
    this.stage = new ArcadeStage({ labels: this.labelsValue, scoresUrl: this.scoresUrlValue, onRestart: () => this.#play(), onClose: () => this.#stop() })
    this.stage.open()
    // Les couleurs de la scène, que les skins du mode fun redonnent (cf. `.arcade-stage`).
    this.palette = arcadePalette(this.stage.root)
    this.#buildCanvas()
    this.#play()
  }

  // --- mise en place ------------------------------------------------------------------

  #buildCanvas() {
    this.canvas = document.createElement("canvas")
    this.canvas.className = "arcade-snake"
    this.stage.board.appendChild(this.canvas)
    // `ctx` et pas `context` : `context` appartient au contrôleur Stimulus (c'est par lui
    // que passent `scope`, `element` et les valeurs). L'écraser rendait `labelsValue`
    // illisible dès le premier accès.
    this.ctx = this.canvas.getContext("2d")

    this.onResize = () => this.#size()
    window.addEventListener("resize", this.onResize)
    this.#size()

    this.onKey = (event) => this.#key(event)
    document.addEventListener("keydown", this.onKey)

    // Au doigt : un glissement dans la direction voulue.
    this.onTouchStart = (event) => {
      const touch = event.changedTouches[0]
      this.touchOrigin = { x: touch.clientX, y: touch.clientY }
    }
    this.onTouchEnd = (event) => this.#swipe(event)
    this.canvas.addEventListener("touchstart", this.onTouchStart, { passive: true })
    this.canvas.addEventListener("touchend", this.onTouchEnd, { passive: true })
  }

  // Le canvas est carré et suit la plus petite dimension disponible, en pixels physiques :
  // sans le `devicePixelRatio`, la grille bavait sur un écran à forte densité.
  #size() {
    if (!this.canvas) return

    const box = this.stage.board.getBoundingClientRect()
    const side = Math.max(240, Math.floor(Math.min(box.width, box.height)) - 8)
    const ratio = window.devicePixelRatio || 1

    this.canvas.style.width = `${side}px`
    this.canvas.style.height = `${side}px`
    this.canvas.width = side * ratio
    this.canvas.height = side * ratio
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    this.cell = side / COLUMNS
    this.side = side
    this.#draw()
  }

  // --- partie -------------------------------------------------------------------------

  #play() {
    this.snake = [{ x: 8, y: 12 }, { x: 7, y: 12 }, { x: 6, y: 12 }]
    this.direction = [1, 0]
    this.queued = null
    this.interval = START_INTERVAL
    this.paused = false
    this.running = false
    this.over = false
    this.stage.setScore(0)
    this.#placeContract()
    this.stage.setHint(this.labelsValue.start || "")
    this.#draw()
  }

  #stop() {
    window.clearTimeout(this.timer)
    window.removeEventListener("resize", this.onResize)
    document.removeEventListener("keydown", this.onKey)
    this.canvas?.removeEventListener("touchstart", this.onTouchStart)
    this.canvas?.removeEventListener("touchend", this.onTouchEnd)
    this.canvas = null
  }

  #key(event) {
    if (event.key === " " || event.code === "Space") {
      event.preventDefault()
      this.#togglePause()
      return
    }

    const direction = DIRECTIONS[event.key] || DIRECTIONS[event.key?.toLowerCase?.()]
    if (!direction) return

    event.preventDefault()
    this.#steer(direction)
  }

  #swipe(event) {
    if (!this.touchOrigin) return

    const touch = event.changedTouches[0]
    const dx = touch.clientX - this.touchOrigin.x
    const dy = touch.clientY - this.touchOrigin.y
    this.touchOrigin = null
    if (Math.hypot(dx, dy) < 24) return

    this.#steer(Math.abs(dx) > Math.abs(dy) ? [Math.sign(dx), 0] : [0, Math.sign(dy)])
  }

  // Demi-tour interdit : sans ce garde-fou, une flèche vers l'arrière tuait le serpent sur
  // son propre cou, ce qui se lit comme un bug et pas comme une faute de pilotage.
  #steer([x, y]) {
    if (this.over) return

    const [cx, cy] = this.queued || this.direction
    if (x === -cx && y === -cy) return

    this.queued = [x, y]
    if (!this.running) this.#run()
  }

  #togglePause() {
    if (!this.running || this.over) return

    this.paused = !this.paused
    this.stage.setHint(this.paused ? this.labelsValue.paused || "" : this.labelsValue.hint || "")
    if (!this.paused) this.#tick()
  }

  #run() {
    this.running = true
    this.stage.setHint(this.labelsValue.hint || "")
    this.#tick()
  }

  #tick() {
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.#step(), this.interval)
  }

  #step() {
    if (this.paused || this.over) return

    if (this.queued) {
      this.direction = this.queued
      this.queued = null
    }

    const head = {
      x: this.snake[0].x + this.direction[0],
      y: this.snake[0].y + this.direction[1],
    }

    if (head.x < 0 || head.y < 0 || head.x >= COLUMNS || head.y >= ROWS) return this.#gameOver()
    if (this.snake.some((part) => part.x === head.x && part.y === head.y)) return this.#gameOver()

    this.snake.unshift(head)

    if (head.x === this.contract.x && head.y === this.contract.y) {
      this.stage.setScore(this.stage.score + 1)
      this.interval = Math.max(MIN_INTERVAL, this.interval - SPEED_STEP)
      this.#placeContract()
    } else {
      this.snake.pop()
    }

    this.#draw()
    this.#tick()
  }

  #gameOver() {
    this.over = true
    window.clearTimeout(this.timer)
    this.stage.finish({ score: this.stage.score })
  }

  // Jamais sous le serpent : sinon le contrat est inatteignable et la partie se bloque.
  #placeContract() {
    const free = []
    for (let x = 0; x < COLUMNS; x += 1) {
      for (let y = 0; y < ROWS; y += 1) {
        if (!this.snake.some((part) => part.x === x && part.y === y)) free.push({ x, y })
      }
    }
    this.contract = free[Math.floor(Math.random() * free.length)]
  }

  // --- rendu --------------------------------------------------------------------------

  #draw() {
    // `#size()` peint dès la mise en place du canvas, avant la première partie : à ce
    // moment-là il n'y a ni serpent ni contrat à dessiner.
    if (!this.ctx || !this.snake || !this.contract) return

    const { ctx: context, cell, side, palette } = this
    context.clearRect(0, 0, side, side)

    context.fillStyle = palette.deep(0.10)
    context.fillRect(0, 0, side, side)

    this.#drawContract(context, cell)
    this.snake.forEach((part, index) => {
      // La tête est pleine, le corps s'éteint doucement : on suit le serpent du regard même
      // quand il remplit la moitié de la grille.
      const fade = 1 - Math.min(index / (this.snake.length + 6), 0.6)
      context.fillStyle = palette.glow(index === 0 ? 1 : fade)
      this.#roundedSquare(context, part.x * cell, part.y * cell, cell)
    })
  }

  // Un contrat, c'est une feuille avec un coin plié : deux rectangles suffisent à le dire.
  #drawContract(context, cell) {
    const x = this.contract.x * cell
    const y = this.contract.y * cell
    const pad = cell * 0.18

    context.fillStyle = this.palette.hot()
    context.fillRect(x + pad, y + pad * 0.6, cell - pad * 2, cell - pad * 1.2)
    context.fillStyle = "rgba(0, 0, 0, 0.35)"
    context.fillRect(x + pad * 1.6, y + cell * 0.38, cell - pad * 3.2, Math.max(1, cell * 0.08))
    context.fillRect(x + pad * 1.6, y + cell * 0.58, cell - pad * 4, Math.max(1, cell * 0.08))
  }

  #roundedSquare(context, x, y, size) {
    const inset = size * 0.08
    const radius = size * 0.24

    // `roundRect` est récent : sur un navigateur qui ne l'a pas, un anneau carré vaut mieux
    // qu'une exception au milieu de la boucle de rendu.
    if (typeof context.roundRect !== "function") {
      context.fillRect(x + inset, y + inset, size - inset * 2, size - inset * 2)
      return
    }

    context.beginPath()
    context.roundRect(x + inset, y + inset, size - inset * 2, size - inset * 2, radius)
    context.fill()
  }
}
